import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../../api.js';
import Field from '../../components/Field.jsx';
import { checkEmail, checkRequired } from '../../utils/validate.js';
import Alert from '../../components/ui/Alert.jsx';
import Modal, { ModalActions } from '../../components/ui/Modal.jsx';
import { EmptyState, Loading, PageHeader, Section } from '../../components/ui/index.jsx';
import { ROLE_LABELS, RolePills, StatusPill, fmtDateTime, fullName } from './adminShared.jsx';

const FILTERS = [
  { key: '', label: 'Everyone' },
  { key: 'active', label: 'Active' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'unconfirmed', label: 'Not confirmed' },
];

const EMPTY_FORM = { first_name: '', last_name: '', email: '', roles: ['user'], send_welcome: true };

function NewUserModal({ onCancel, onCreated }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); if (errors[k]) setErrors({ ...errors, [k]: undefined }); };
  const toggleRole = (r) => {
    setForm({ ...form, roles: form.roles.includes(r) ? form.roles.filter((x) => x !== r) : [...form.roles, r] });
    if (errors.roles) setErrors({ ...errors, roles: undefined });
  };

  const submit = async () => {
    const found = Object.fromEntries(Object.entries({
      first_name: checkRequired(form.first_name, 'their first name', { max: 100 }),
      last_name: checkRequired(form.last_name, 'their last name', { max: 100 }),
      email: checkEmail(form.email, { label: 'their email address' }),
      roles: form.roles.length ? '' : 'Choose at least one role.',
    }).filter(([, m]) => m));
    if (Object.keys(found).length) { setErrors(found); return; }
    setBusy(true); setError('');
    try {
      const res = await api('/admin/users', { method: 'POST', body: form });
      onCreated(res);
    } catch (err) {
      setErrors(err.data?.fields || {});
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Add a user" onClose={onCancel} onSubmit={submit} busy={busy}
      description="They won’t get a password from you. We email them a link to set their own, which asks for a code sent to their inbox."
      footer={<ModalActions submitLabel="Add user" busyLabel="Adding…" busy={busy} onCancel={onCancel} />}>
      {error && !Object.keys(errors).length && <Alert tone="danger">{error}</Alert>}
      <div className="row-2">
        <Field label="First name" required maxLength={100} value={form.first_name} onChange={set('first_name')} error={errors.first_name} />
        <Field label="Last name" required maxLength={100} value={form.last_name} onChange={set('last_name')} error={errors.last_name} />
      </div>
      <Field label="Email address" type="email" autoCapitalize="none" required value={form.email} onChange={set('email')} error={errors.email}
        onBlur={() => form.email.trim() && setErrors((x) => ({ ...x, email: checkEmail(form.email) || undefined }))} />
      <fieldset className="fieldset">
        <legend>Role</legend>
        {Object.entries(ROLE_LABELS).map(([key, label]) => (
          <label key={key} className="checkbox">
            <input type="checkbox" checked={form.roles.includes(key)} onChange={() => toggleRole(key)} />
            <span><strong>{label}</strong>{key === 'admin' ? ' — full access to this admin area' : ' — uses the portal to protect their events'}</span>
          </label>
        ))}
        {errors.roles && <p className="field-error">{errors.roles}</p>}
      </fieldset>
      <label className="checkbox">
        <input type="checkbox" checked={form.send_welcome} onChange={(e) => setForm({ ...form, send_welcome: e.target.checked })} />
        <span>Email them a link to set their password</span>
      </label>
    </Modal>
  );
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async (query, st, pg) => {
    setError('');
    try {
      const params = new URLSearchParams({ q: query, page: String(pg) });
      if (st) params.set('status', st);
      setData(await api(`/admin/users?${params}`));
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // Search as you type, with a short pause so we don't query on every keystroke
  useEffect(() => {
    const t = setTimeout(() => load(q, status, page), 250);
    return () => clearTimeout(t);
  }, [q, status, page, load]);

  const created = ({ user, welcome_email: welcome }) => {
    navigate(`/admin/users/${user.id}`, { state: { created: true, welcome } });
  };

  return (
    <div className="page">
      <PageHeader title="Users"
        subtitle="Everyone with a SummitShield account. Open someone to edit them, reset their password, block or delete them."
        actions={<button type="button" className="btn btn-primary" onClick={() => setAdding(true)}>Add user</button>} />

      <div className="toolbar">
        <div className="field search-field">
          <label htmlFor="user-search" className="sr-only">Search users</label>
          <input id="user-search" type="search" placeholder="Search by name or email" value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }} />
        </div>
        <div className="segmented" role="group" aria-label="Filter by status">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" className={status === f.key ? 'is-active' : ''}
              aria-pressed={status === f.key} onClick={() => { setStatus(f.key); setPage(1); }}>{f.label}</button>
          ))}
        </div>
      </div>

      {error && <Alert tone="danger">{error}</Alert>}
      {!data && !error && <Loading />}
      {data && (
        <Section flush={data.users.length > 0}>
          {data.users.length === 0 ? (
            <EmptyState>No users match.</EmptyState>
          ) : (
            <div className="table-scroll">
              <table className="table table-stack">
                <thead>
                  <tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Last signed in</th></tr>
                </thead>
                <tbody>
                  {data.users.map((u) => (
                    <tr key={u.id}>
                      <td data-label="Name"><Link className="strong-link" to={`/admin/users/${u.id}`}>{fullName(u)}</Link></td>
                      <td data-label="Email" className="mono small">{u.email}</td>
                      <td data-label="Role"><RolePills roles={u.roles} /></td>
                      <td data-label="Status">
                        <StatusPill status={u.status} />
                        {!u.has_password && u.status === 'active' && <span className="muted small"> · no password yet</span>}
                      </td>
                      <td data-label="Last signed in" className="small">{fmtDateTime(u.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Section>
      )}
      {data && (page > 1 || data.has_more) && (
        <div className="pager">
          <button type="button" className="btn btn-secondary btn-small" disabled={page === 1} onClick={() => setPage(page - 1)}>Previous</button>
          <span className="muted small">Page {page}</span>
          <button type="button" className="btn btn-secondary btn-small" disabled={!data.has_more} onClick={() => setPage(page + 1)}>Next</button>
        </div>
      )}

      {adding && <NewUserModal onCancel={() => setAdding(false)} onCreated={created} />}
    </div>
  );
}
